/*
  Controller voor sections/ss-product-vidoes.liquid.
  Stond als ~18KB inline <script> in de sectie en ging dus bij elke PDP-view
  opnieuw over de lijn; hier is het één gecachet bestand voor negen templates.

  Settings komen uit een <div data-resty-product-videos> in de sectie; de
  parse-tijd-injectie van de videostrip blijft daar inline staan, want die moet
  vóór dit defer-script lopen (posters in dezelfde HTML-golf).

  Swiper zelf komt van assets/resty-swiper-loader.js; de sectie zet die tag in
  documentorde hierboven, en defer-scripts lopen in die orde.
*/
(() => {
  const setup = (config) => {
    const sectionId = config.dataset.sectionId;
    const muteLabel = config.dataset.labelMute;
    const unmuteLabel = config.dataset.labelUnmute;
    const previousLabel = config.dataset.labelPrevious;
    const nextLabel = config.dataset.labelNext;
    const sliderGap = Number(config.dataset.sliderGap) || 0;
    const sliderGapMobile = Number(config.dataset.sliderGapMobile) || 0;

    let slider = null;
    let modalSlider = null;
    let lastFocusedElement = null;
    let previousBodyOverflow = '';

    const safePlay = (video, soundButton = null) => {
      const playPromise = video.play();
      if (!playPromise || typeof playPromise.catch !== 'function') return;
      playPromise.catch(() => {
        video.muted = true;
        updateSoundButton(soundButton, false);
        video.play().catch(() => {});
      });
    };

    const updateSoundButton = (button, soundOn) => {
      if (!button) return;
      button.classList.toggle('active', soundOn);
      button.setAttribute('aria-pressed', String(soundOn));
      button.setAttribute('aria-label', soundOn ? muteLabel : unmuteLabel);
    };

    const updatePlayButton = (button, isPlaying) => {
      if (!button) return;
      button.classList.toggle('is-paused', !isPlaying);
      button.setAttribute('aria-pressed', String(isPlaying));
      button.setAttribute(
        'aria-label',
        isPlaying ? button.dataset.labelPause : button.dataset.labelPlay
      );
    };

    const postFrameCommand = (frame, command) => {
      if (!frame || !frame.contentWindow) return;
      const source = frame.getAttribute('src') || frame.dataset.src || '';
      const isYouTube = source.includes('youtube.com') || source.includes('youtube-nocookie.com');
      const message = isYouTube
        ? { event: 'command', func: command === 'play' ? 'playVideo' : 'pauseVideo', args: '' }
        : { method: command };
      frame.contentWindow.postMessage(JSON.stringify(message), '*');
    };

    const setMediaSource = (media, source) => {
      if (!media || !source || media.getAttribute('src') === source) return;
      media.setAttribute('src', source);
      media.dataset.restyHydrated = 'true';
      if (media.tagName === 'VIDEO') media.load();
    };

    function initProductVideos() {
      const videoSection = document.querySelector(`.video-${sectionId}`);
      const modal = document.querySelector(`.video-modal-${sectionId}`);
      // Dit script is defer: de DOM is klaar en de parse-tijd-injectie is al gelopen.
      // Ontbreekt de strip hier, dan is de injectie definitief mislukt.
      if (!videoSection || !modal) return;
      if (videoSection.dataset.initialized === 'true') return;
      videoSection.dataset.initialized = 'true';

      const sliderElement = videoSection.querySelector(`.video-slider-${sectionId}`);
      const modalSliderElement = modal.querySelector(`.video-modal-slider-${sectionId}`);
      const closeButton = modal.querySelector(`.video-modal-close-${sectionId}`);
      const modalSlides = Array.from(modal.querySelectorAll(`.video-modal-slide-${sectionId}`));
      const openButtons = Array.from(videoSection.querySelectorAll(`.video-open-modal-${sectionId}`));

      const deactivateModalSlide = (slide) => {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('inert', '');
        const media = slide.querySelector('video, iframe');
        const soundButton = slide.querySelector(`.video-modal-action-${sectionId}`);
        const playButton = slide.querySelector(`.video-modal-play-${sectionId}`);
        updateSoundButton(soundButton, false);
        updatePlayButton(playButton, false);
        if (!media) return;

        if (media.tagName === 'VIDEO') {
          media.pause();
          media.currentTime = 0;
          media.removeAttribute('src');
          delete media.dataset.restyHydrated;
          // Abort een lopende mediarequest en reset de media state.
          media.load();
        } else if (media.dataset.restyHydrated === 'true') {
          // Alleen src leegmaken stopt een YouTube/Vimeo-embed echt;
          // removeAttribute laat het geladen document (en audio) doorlopen.
          media.setAttribute('src', 'about:blank');
          delete media.dataset.restyHydrated;
        }
      };

      const activateModalSlide = (index) => {
        modalSlides.forEach((slide, slideIndex) => {
          if (slideIndex !== index) {
            deactivateModalSlide(slide);
            return;
          }

          slide.removeAttribute('inert');
          slide.setAttribute('aria-hidden', 'false');
          const media = slide.querySelector('video, iframe');
          const soundButton = slide.querySelector(`.video-modal-action-${sectionId}`);
          const playButton = slide.querySelector(`.video-modal-play-${sectionId}`);
          if (!media) return;

          updatePlayButton(playButton, false);

          const source = media.tagName === 'IFRAME'
            ? (media.dataset.src2 || media.dataset.src)
            : media.dataset.src;
          // Poster pas koppelen bij activatie; in de verborgen modal zou het
          // attribuut de 900px-beelden al vóór een klik laten downloaden.
          if (media.tagName === 'VIDEO' && media.dataset.poster && !media.getAttribute('poster')) {
            media.setAttribute('poster', media.dataset.poster);
          }
          setMediaSource(media, source);

          if (media.tagName === 'VIDEO') {
            media.muted = false;
            safePlay(media, soundButton);
          } else {
            updatePlayButton(playButton, true);
          }
          updateSoundButton(soundButton, true);
        });
      };

      slider = new Swiper(sliderElement, {
        speed: 300,
        slidesPerView: 'auto',
        watchSlidesProgress: true,
        a11y: {
          prevSlideMessage: previousLabel,
          nextSlideMessage: nextLabel,
        },
        navigation: {
          nextEl: videoSection.querySelector(`.video-btn-next-${sectionId}`),
          prevEl: videoSection.querySelector(`.video-btn-prev-${sectionId}`),
        },
        breakpoints: {
          320: { spaceBetween: sliderGapMobile },
          768: { spaceBetween: sliderGapMobile },
          1024: { spaceBetween: sliderGap },
        },
      });

      modalSlider = new Swiper(modalSliderElement, {
        speed: 300,
        slidesPerView: 1,
        allowTouchMove: false,
        spaceBetween: 20,
        a11y: {
          prevSlideMessage: previousLabel,
          nextSlideMessage: nextLabel,
        },
        navigation: {
          nextEl: modal.querySelector(`.video-modal-next-${sectionId}`),
          prevEl: modal.querySelector(`.video-modal-prev-${sectionId}`),
        },
        on: {
          slideChange() {
            if (!modal.hidden) activateModalSlide(this.activeIndex);
          },
        },
      });

      modalSlides.forEach((slide) => {
        const media = slide.querySelector('video, iframe');
        const playButton = slide.querySelector(`.video-modal-play-${sectionId}`);
        if (!media || !playButton) return;

        updatePlayButton(playButton, false);
        if (media.tagName !== 'VIDEO') return;
        media.addEventListener('play', () => updatePlayButton(playButton, true));
        media.addEventListener('pause', () => updatePlayButton(playButton, false));
        media.addEventListener('ended', () => updatePlayButton(playButton, false));
      });

      /* Poster-first invariant (perf-audit juli 2026): previews worden nooit
         gehydrateerd: het IntersectionObserver-pad dat bij scrollen bronnen
         koppelde en autoplay startte is verwijderd. Alleen de actieve
         modalslide krijgt na een klik een src. */

      window.addEventListener('pagehide', () => {
        modalSlides.forEach(deactivateModalSlide);
      });

      /* Vangnet voor de afgeleide ≤720p-bron: faalt die (404), schakel dan
         over op de originele video.sources-URL en speel verder. */
      modal.querySelectorAll('video[data-src-full]').forEach((media) => {
        media.addEventListener('error', () => {
          const fullSource = media.dataset.srcFull;
          const currentSource = media.getAttribute('src');
          if (!fullSource || !currentSource || currentSource === fullSource) return;
          // Onthoud de fallback ook in data-src, zodat heropenen van de
          // modal niet opnieuw eerst de falende URL probeert.
          media.dataset.src = fullSource;
          setMediaSource(media, fullSource);
          safePlay(media);
        });
      });

      const closeModal = () => {
        if (modal.hidden) return;
        modal.classList.remove('active');
        modalSlides.forEach(deactivateModalSlide);
        modal.hidden = true;
        document.body.style.overflow = previousBodyOverflow;
        if (lastFocusedElement && document.contains(lastFocusedElement)) lastFocusedElement.focus();
      };

      const openModal = (index, trigger) => {
        lastFocusedElement = trigger;
        previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        modal.hidden = false;
        modal.classList.add('active');
        modalSlider.update();
        modalSlider.slideTo(index, 0, false);
        activateModalSlide(index);
        window.requestAnimationFrame(() => closeButton.focus());
      };

      openButtons.forEach((button, index) => {
        button.addEventListener('click', () => openModal(index, button));
      });

      modal.querySelectorAll(`.video-modal-play-${sectionId}`).forEach((button) => {
        button.addEventListener('click', () => {
          const slide = button.closest(`.video-modal-slide-${sectionId}`);
          const media = slide?.querySelector('video, iframe');
          const soundButton = slide?.querySelector(`.video-modal-action-${sectionId}`);
          if (!media || slide.getAttribute('aria-hidden') === 'true') return;

          const isPlaying = media.tagName === 'VIDEO'
            ? !media.paused && !media.ended
            : !button.classList.contains('is-paused');

          if (isPlaying) {
            if (media.tagName === 'VIDEO') {
              media.pause();
            } else {
              postFrameCommand(media, 'pause');
              updatePlayButton(button, false);
            }
          } else if (media.tagName === 'VIDEO') {
            safePlay(media, soundButton);
          } else {
            postFrameCommand(media, 'play');
            updatePlayButton(button, true);
          }
        });
      });

      modal.querySelectorAll(`.video-modal-action-${sectionId}`).forEach((button) => {
        button.addEventListener('click', () => {
          const slide = button.closest(`.video-modal-slide-${sectionId}`);
          const media = slide?.querySelector('video, iframe');
          const playButton = slide?.querySelector(`.video-modal-play-${sectionId}`);
          if (!media || slide.getAttribute('aria-hidden') === 'true') return;

          const soundOn = !button.classList.contains('active');
          if (media.tagName === 'IFRAME') {
            setMediaSource(media, soundOn ? (media.dataset.src2 || media.dataset.src) : media.dataset.src);
            updatePlayButton(playButton, true);
          } else {
            media.muted = !soundOn;
            if (soundOn) safePlay(media, button);
          }
          updateSoundButton(button, soundOn);
        });
      });

      closeButton.addEventListener('click', closeModal);
      modal.addEventListener('click', (event) => {
        if (event.target === modal) closeModal();
      });
      modal.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeModal();
          return;
        }
        if (event.key !== 'Tab') return;

        const focusable = Array.from(modal.querySelectorAll(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )).filter((element) => !element.closest('[inert]'));
        if (!focusable.length) {
          event.preventDefault();
          modal.focus();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    const destroyProductVideos = () => {
      if (slider && !slider.destroyed) slider.destroy(true, true);
      if (modalSlider && !modalSlider.destroyed) modalSlider.destroy(true, true);
      const modal = document.querySelector(`.video-modal-${sectionId}`);
      const videoSection = document.querySelector(`.video-${sectionId}`);
      const modalWasOpen = modal && !modal.hidden;
      let focusTarget = null;

      if (modalWasOpen) {
        document.body.style.overflow = previousBodyOverflow;
        if (
          lastFocusedElement
          && document.contains(lastFocusedElement)
          && !videoSection?.contains(lastFocusedElement)
        ) {
          focusTarget = lastFocusedElement;
        } else {
          focusTarget = document.querySelector('main, [role="main"]') || document.body;
        }
      }

      modal?.remove();
      videoSection?.remove();
      if (focusTarget) {
        const previousTabindex = focusTarget.getAttribute('tabindex');
        focusTarget.setAttribute('tabindex', '-1');
        focusTarget.focus({ preventScroll: true });
        if (previousTabindex === null) {
          focusTarget.removeAttribute('tabindex');
        } else {
          focusTarget.setAttribute('tabindex', previousTabindex);
        }
      }
      slider = null;
      modalSlider = null;
      lastFocusedElement = null;
    };

    const start = () => {
      window.RestySwiperLoader.load()
        .then(initProductVideos)
        .catch((error) => console.error(error));
    };

    /* Swiper pas laden zodra de slider echt nadert. Op mobiel staat deze
       sectie circa 600 px onder de eerste viewport; met dezelfde rootMargin
       werd de bundel daarom alsnog tijdens de eerste paint opgehaald. 200 px
       geeft de browser nog voorlaadtijd tijdens het scrollen, zonder de
       initiële PDP-route met slidercode te belasten. */
    const startWhenVisible = () => {
      const target = document.querySelector(`.video-${sectionId}`);
      if (!target || !('IntersectionObserver' in window)) {
        start();
        return;
      }
      const loadObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadObserver.disconnect();
          start();
        }
      }, { rootMargin: '200px 0px' });
      loadObserver.observe(target);
    };

    startWhenVisible();

    if (window.Shopify && Shopify.designMode) {
      // section:load wordt door boot() opgevangen: de sectie krijgt verse DOM,
      // dus een verse carrier zonder restyBooted.
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.detail.sectionId === sectionId) destroyProductVideos();
      });
    }
  };

  const boot = () => {
    document.querySelectorAll('[data-resty-product-videos]').forEach((config) => {
      if (config.dataset.restyBooted === 'true') return;
      config.dataset.restyBooted = 'true';
      setup(config);
    });
  };

  // defer garandeert dat de DOM klaar is; de guard dekt een section:load in de editor.
  boot();
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', boot);
  }
})();
